/**
 * Auth composition root.
 *
 * Wires ports to adapters and exposes a single {@link initAuth} entry point
 * that the host application calls once at boot (typically in `_app.tsx`).
 *
 * This module is intentionally thin — strategy selection lives in
 * `auth-service-resolver.ts`, HTTP client construction in `http/`, and
 * header logic in `http/headers.ts`.
 *
 * @module
 */
import Cookies from 'js-cookie';
import { CookieSessionPersistence } from './session-persistence';
import { SessionStorageAdapter } from './session-store';
import { createAuthLogger } from '../shared/auth-logger';
import { createAuthSession, type AuthSession } from '../flows/auth-session';
import { buildHttpClients, _resetHeaderCacheForTests } from './http';
import { resolveAuthService } from './auth-service-resolver';
import type { TranslationPort, User } from '../types';
import type { SocialAuthListener } from '../types/ports';
import type { CognitoConfig } from './cognito-config';
import type { MergeAmplifyConfigFn } from './amplify/config';
import { AuthObservabilityServiceImpl, type TrackEventFn } from '../observability/service';
import { FactoryEvent } from '../constants';

/** Options accepted by {@link initAuth}. */
interface AuthInitOptions {
  /** Whether the `use-cognito-auth` GrowthBook flag is enabled. */
  useCognitoAuth: boolean;
  /** Current tenant theme name (e.g. `'zoocasa'`, `'exp-realty-ca'`). */
  themeName: string;
  /** i18n translation port for localised error messages. */
  translation: TranslationPort;
  /** Server-side rendered user, if available from the initial page load. */
  ssrUser: User | null;
  /** Primary client-app host (e.g. `https://www.zoocasa.com`). */
  clientAppHost?: string;
  /** Optional auth-API host override.  Defaults to `clientAppHost`. */
  authApiHost?: string;
  /** Enables verbose `[auth]` console logging when `true`. */
  debug?: boolean;
  /** Cognito pool / domain configuration.  Required when {@link useCognitoAuth} is `true`. */
  cognitoConfig?: CognitoConfig;
  /** Amplify SDK config-merge callback.  Required when {@link useCognitoAuth} is `true`. */
  mergeAmplifyConfig?: MergeAmplifyConfigFn;
  /** Host-supplied analytics callback forwarded to the observability service. */
  trackEvent?: TrackEventFn;
  /** Callback invoked after social sign-in to restore the pre-redirect URL. */
  restoreReturnTo?: (target: string) => void;
  /**
   * When `true`, auth cookies are written with the `Secure` flag. Set to
   * `false` in local development (HTTP) so the browser accepts the cookies.
   * Defaults to `true`.
   */
  secureCookies?: boolean;
}

/**
 * Boot sequence for the auth system.
 *
 * Returns a **new** session every time — the caller is responsible for
 * holding a stable reference (e.g. via `useRef` in `_app.tsx`).
 */
export function initAuth(options: AuthInitOptions): AuthSession {
  const debug = options.debug ?? false;

  // ── 1. Boot logger ──────────────────────────────────────────────────────
  const logger = createAuthLogger(debug);

  // ── 2. Build network layer ──────────────────────────────────────────────
  const clients = buildHttpClients(options);

  // ── 3. Mount persistence adapters ───────────────────────────────────────
  const persistence = new CookieSessionPersistence(Cookies, options.secureCookies ?? true);
  const sessionStore = new SessionStorageAdapter();

  // ── 4. Arm observability ────────────────────────────────────────────────
  const observability = options.trackEvent
    ? new AuthObservabilityServiceImpl(options.trackEvent)
    : undefined;

  // ── 5. Wire social auth bridge ──────────────────────────────────────────
  // Amplify Hub events fire after the session exists, so we late-bind.
  let sessionRef: AuthSession | null = null;
  const socialProxy: SocialAuthListener = {
    setSocialRedirectState: s => sessionRef!.setSocialRedirectState(s),
    cognitoSocialSignInComplete: () => sessionRef!.cognitoSocialSignInComplete(),
    legacySdkSignInComplete: p => sessionRef!.legacySdkSignInComplete(p),
    failSocialAuth: e => sessionRef!.failSocialAuth(e),
  };

  // ── 6. Resolve auth strategy ────────────────────────────────────────────
  const { authService, amplifyConfigured } = resolveAuthService(
    {
      useCognitoAuth: options.useCognitoAuth,
      themeName: options.themeName,
      translation: options.translation,
      cognitoConfig: options.cognitoConfig,
      mergeAmplifyConfig: options.mergeAmplifyConfig,
    },
    {
      clients,
      logger,
      persistence,
      observability,
      socialAuthListener: socialProxy,
    },
  );

  // ── 7. Spawn session ────────────────────────────────────────────────────
  const session = createAuthSession({
    authService,
    persistence,
    sessionStore,
    ssrUser: options.ssrUser,
    debug,
    restoreReturnTo: options.restoreReturnTo,
    observability,
  });
  sessionRef = session;

  // ── 8. Ready ────────────────────────────────────────────────────────────
  logger.log(FactoryEvent.Assembled, {
    service: options.useCognitoAuth ? 'cognito' : 'client-app',
    amplifyConfigured,
    hasObservability: Boolean(observability),
    hasSsrUser: Boolean(options.ssrUser),
  });

  return session;
}

/**
 * Resets internal caches so the next {@link initAuth} call starts clean.
 * **Test use only** — never call this in production code.
 */
export function _resetInitAuthForTests(): void {
  _resetHeaderCacheForTests();
}
